import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { Info, Lock, Loader2, RefreshCw } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { formatDate } from "../../utils/format";
import { opts } from "./constants";

// Fix CC (2026-09-10): "base madre" (CEN EAS, 10/09). Dentro de un presupuesto, las
// pestañas Bocas / Mano de Obra / Catálogos, los Parámetros de Jornal, el buscador
// de precios de la Estructura de Cómputo y las marcas del autollenado trabajan sobre
// la COPIA de ese presupuesto (/api/presupuestos/{id}/...). Fuera de un presupuesto
// (Presupuestos → Base Madre, Listas de Precios) trabajan sobre la base madre
// (/api/bocas-precios, /api/catalogos, /api/settings/jornales-mo), como siempre.
// BudgetForm provee el id del presupuesto. Uno nuevo que todavía no se guardó provee
// PRESUPUESTO_SIN_GUARDAR: sus pestañas muestran un aviso en vez de editar la base, y
// el buscador de precios lee la base madre, que es lo que se le copia al guardarlo.
export const PRESUPUESTO_SIN_GUARDAR = "__sin_guardar__";
export const CatalogoPresupuestoContext = createContext(null);

export function useCatalogoPresupuesto() {
  const valor = useContext(CatalogoPresupuestoContext);
  const budgetId = valor && valor !== PRESUPUESTO_SIN_GUARDAR ? valor : null;
  return {
    budgetId,
    sinGuardar: valor === PRESUPUESTO_SIN_GUARDAR,
    catApi: budgetId ? `${API}/presupuestos/${budgetId}` : API,
  };
}

export function CatalogoSinGuardar({ que }) {
  return (
    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-500" data-testid="catalogo-sin-guardar">
        <Lock size={28} className="mx-auto mb-3 text-gray-300" />
        <p className="font-semibold text-gray-700">{que} de este presupuesto se editan después de guardarlo</p>
        <p className="text-xs mt-2 max-w-md mx-auto">
          Al guardar el presupuesto se le copia la base madre. Desde ahí, lo que cambies en esta pestaña queda solo en este presupuesto.
        </p>
        <p className="text-xs mt-2 text-gray-400">La base madre se edita desde Presupuestos → Base Madre.</p>
      </div>
    </div>
  );
}

export function CatalogoScopeBanner({ onRecopiado }) {
  const { budgetId, catApi } = useCatalogoPresupuesto();
  const [estado, setEstado] = useState(null);
  const [trayendo, setTrayendo] = useState(false);

  const cargar = useCallback(() => {
    if (!budgetId) return;
    axios.get(`${catApi}/catalogo/estado`, opts).then(({ data }) => setEstado(data)).catch(() => setEstado(null));
  }, [budgetId, catApi]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!budgetId) return null;

  const traerBase = async () => {
    if (!window.confirm(
      "¿Traer la base madre actual a este presupuesto?\n\n" +
      "Las bocas, rubros de M.O., ítems de catálogo y jornales que están en la base madre se reemplazan por su versión de hoy: " +
      "se pierden los cambios que les hiciste en este presupuesto y vuelven los que hayas borrado acá. " +
      "Lo que creaste solo en este presupuesto se conserva.\n\n" +
      "Los precios ya cargados en la Estructura de Cómputo no cambian."
    )) return;
    setTrayendo(true);
    try {
      const { data } = await axios.post(`${catApi}/catalogo/recopiar-base`, {}, opts);
      setEstado(data);
      toast.success("Base madre traída a este presupuesto");
      if (onRecopiado) onRecopiado();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo traer la base madre");
    } finally { setTrayendo(false); }
  };

  return (
    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-900" data-testid="catalogo-scope-banner">
      <Info size={14} className="flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p><b>Copia propia de este presupuesto.</b> Lo que cambies acá no toca la base madre ni los demás presupuestos.</p>
        {estado?.copiado_at && (
          <p className="text-amber-700 mt-0.5" data-testid="catalogo-scope-origen">
            Copiada {estado.origen_label} el {formatDate(estado.copiado_at)}
            {estado.base_traida_at ? ` · base madre traída de nuevo el ${formatDate(estado.base_traida_at)}` : ""}.
          </p>
        )}
      </div>
      {estado?.puede_recopiar && (
        <button onClick={traerBase} disabled={trayendo}
          className="flex items-center gap-1.5 border border-amber-300 bg-white hover:bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex-shrink-0"
          data-testid="catalogo-traer-base-btn">
          {trayendo ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Traer la base madre actual
        </button>
      )}
    </div>
  );
}
